// eslint-disable-next-line import/no-nodejs-modules
import { ParsedUrlQuery } from 'querystring';
import { GetServerSideProps, NextPage } from 'next';
import React from 'react';
import AgentLayoutRedesigned from '../../components/agent/AgentLayoutRedesigned';
import Container from '../../components/Container';
import CostEstimation from '../../components/forms/CostEstimation';
import Seo from '../../components/Seo';
import ContentfulService from '../../services/ContentfulService';
import { CommonPageDataProps, MortgageCalculatorPageQuery } from '../../types';
import {
  IProperty,
  RealApiAgentWebsiteResponse,
} from '../../types/real-api-types';
import { getAgentWebsiteApiResponse } from '../../utils/agentWebsiteUtils';
import { getPropertyDetail } from '../../utils/PropertiesUtils';
import { OfficesResponse } from '../../openapi/bff';
import BFFApiService from '../../services/BFFApiService';

interface MortgageCalculatorPageParams extends ParsedUrlQuery {
  agentSlug: string;
}

interface MortgageCalculatorPageProps extends CommonPageDataProps {
  agent: RealApiAgentWebsiteResponse;
  property?: IProperty;
  allOffices: OfficesResponse;
}

const MortgageCalculatorPage: NextPage<MortgageCalculatorPageProps> = ({
  countriesWithStates,
  footerContent,
  agent,
  property,
  allOffices,
}) => {
  return (
    <AgentLayoutRedesigned
      agentWebsiteResponse={agent}
      countriesWithStates={countriesWithStates}
      footerContent={footerContent}
      showFooterCTA
      allOffices={allOffices}
    >
      <Seo
        title={`${agent.agent.name} - Mortgage Calculator`}
        description='Calculate the payments for a property mortgage'
        imageUrl='/img/default-socialimage.png'
      />
      <Container>
        <div className='w-full max-w-4xl mx-auto'>
          <p className='font-primary-medium md:text-5xl text-2xl py-10'>
            Mortgage Calculator
          </p>
          <div className='flex md:flex-row flex-col w-full'>
            <div className='md:flex-grow mb-10'>
              <CostEstimation listPrice={property?.list_price} />
            </div>
          </div>
        </div>
      </Container>
    </AgentLayoutRedesigned>
  );
};

export const getServerSideProps: GetServerSideProps<
  MortgageCalculatorPageProps,
  MortgageCalculatorPageParams
> = async (ctx) => {
  const contentfulService = new ContentfulService();
  const bffApiService = new BFFApiService();

  let property;
  const { mlsnum, mls_id } = ctx.query as MortgageCalculatorPageQuery;

  const [agentWebsiteResponse, countriesWithStates, footerContent, allOffices] =
    await Promise.all([
      getAgentWebsiteApiResponse(ctx.params.agentSlug),
      contentfulService.getCountriesWithStates(),
      contentfulService.getFooterContent(),
      bffApiService.fetchAllOfficialOffices(),
    ]);

  if (!agentWebsiteResponse) {
    return { notFound: true };
  }

  if (mlsnum && mls_id) {
    property = await getPropertyDetail(mls_id, mlsnum);
  }

  return {
    props: {
      property: (property && property[0]) || null,
      agent: agentWebsiteResponse,
      countriesWithStates,
      footerContent,
      allOffices,
    },
  };
};

export default MortgageCalculatorPage;
